// CoT-meldingstyper utover spor: GeoChat (ATAK-chat), nødvarsel (911/emergency)
// og stridsmelding→CoT-markør. Dette lar SKADE utveksle chat, nød og C2-
// meldinger med ATAK/WinTAK/TAK Server — ikke bare posisjoner.
//
// Ren, avhengighetsfri XML (samme escape-regime som cot.ts). All felt-/ekstern
// tekst XML-escapes; parserne er fokuserte og avviser ugyldig input (null).
import { xmlEsc } from './cot.js';
import { reportSummary } from './report.js';
function iso(ms) {
    const d = new Date(ms);
    return Number.isNaN(d.getTime()) ? new Date(0).toISOString() : d.toISOString();
}
function attr(xml, name) {
    const m = new RegExp(`\\b${name}="([^"]*)"`).exec(xml);
    return m ? m[1] : undefined;
}
function xmlUnesc(s) {
    return s
        .replace(/&lt;/g, '<').replace(/&gt;/g, '>')
        .replace(/&quot;/g, '"').replace(/&apos;/g, "'")
        .replace(/&amp;/g, '&');
}
/** Maks lengde på en enkelt CoT-melding vi parser (DoS-vern). */
export const MAX_COT_MSG_BYTES = 64 * 1024;
const clamp = (s, n) => (s.length > n ? s.slice(0, n) : s);
/** GeoChat → CoT-event (XML). Følger ATAK sitt __chat/remarks-oppsett. */
export function buildGeoChatCot(c, opts = {}) {
    const t = iso(c.tsMs);
    const stale = iso(c.tsMs + (opts.staleSec ?? 86400) * 1000);
    const id = clamp(c.id, 128);
    const room = clamp(c.room, 256);
    const senderCallsign = clamp(c.senderCallsign, 256);
    const text = clamp(c.text, 8000);
    const uid = `GeoChat.${c.senderUid}.${room}.${id}`;
    const lat = c.lat ?? 0;
    const lon = c.lon ?? 0;
    const dest = c.destUid ?? room;
    return (`<event version="2.0" uid="${xmlEsc(uid)}" type="b-t-f"` +
        ` time="${t}" start="${t}" stale="${stale}" how="h-g-i-g-o">` +
        `<point lat="${lat}" lon="${lon}" hae="9999999.0" ce="9999999.0" le="9999999.0"/>` +
        `<detail>` +
        // messageId er en SKADE-utvidelse: garanterer tapsfri id-rundtur selv om
        // room/senderUid inneholder «.» (ATAK ignorerer ukjente attributter).
        `<__chat parent="RootContactGroup" groupOwner="false" chatroom="${xmlEsc(room)}"` +
        ` id="${xmlEsc(room)}" messageId="${xmlEsc(id)}" senderCallsign="${xmlEsc(senderCallsign)}">` +
        `<chatgrp uid0="${xmlEsc(c.senderUid)}" uid1="${xmlEsc(dest)}" id="${xmlEsc(room)}"/>` +
        `</__chat>` +
        `<link uid="${xmlEsc(c.senderUid)}" type="a-f-G-U-C" relation="p-p"/>` +
        `<remarks source="BAO.F.SKADE.${xmlEsc(c.senderUid)}" to="${xmlEsc(dest)}" time="${t}">${xmlEsc(text)}</remarks>` +
        `</detail></event>`);
}
/** Parser en GeoChat-CoT-event. null hvis det ikke er en chat-event. */
export function parseGeoChatCot(xml) {
    if (xml.length > MAX_COT_MSG_BYTES)
        return null;
    const ev = /<event\b[^>]*>/i.exec(xml);
    if (!ev || attr(ev[0], 'type') !== 'b-t-f')
        return null;
    const chat = /<__chat\b[^>]*>/i.exec(xml);
    const rem = /<remarks\b[^>]*>([\s\S]*?)<\/remarks>/i.exec(xml);
    if (!chat || !rem)
        return null;
    const grp = /<chatgrp\b[^>]*>/i.exec(xml);
    const uid = attr(ev[0], 'uid') ?? '';
    const room = attr(chat[0], 'chatroom') ?? attr(chat[0], 'id') ?? '';
    const senderCallsign = attr(chat[0], 'senderCallsign') ?? '';
    const senderUid = (grp && attr(grp[0], 'uid0')) || '';
    const destUid = grp ? attr(grp[0], 'uid1') : undefined;
    const pt = /<point\b[^>]*\/?>/i.exec(xml);
    const lat = pt ? Number(attr(pt[0], 'lat')) : NaN;
    const lon = pt ? Number(attr(pt[0], 'lon')) : NaN;
    const timeStr = attr(rem[0], 'time') ?? attr(ev[0], 'time');
    const tsMs = timeStr ? Date.parse(timeStr) : NaN;
    // Foretrekk messageId (SKADE-utvidelse, tapsfri). Fallback: uid-hale, som
    // kun stemmer når room/senderUid er dot-frie (GeoChat.<sender>.<room>.<id>).
    const idAttr = attr(chat[0], 'messageId');
    const idFromUid = idAttr ? xmlUnesc(idAttr) : (uid.split('.').slice(3).join('.') || uid);
    return {
        id: idFromUid,
        senderUid,
        senderCallsign,
        room,
        destUid: destUid && destUid !== room ? destUid : undefined,
        text: xmlUnesc(rem[1]).trim(),
        tsMs: Number.isFinite(tsMs) ? tsMs : 0,
        lat: Number.isFinite(lat) && lat >= -90 && lat <= 90 ? lat : undefined,
        lon: Number.isFinite(lon) && lon >= -180 && lon <= 180 ? lon : undefined,
    };
}
/** Nødvarsel → CoT. active=false gir kansellerings-event (b-a-o-can). */
export function buildEmergencyCot(e, opts = {}) {
    const t = iso(e.tsMs);
    const stale = iso(e.tsMs + (opts.staleSec ?? 300) * 1000);
    const type = e.active ? 'b-a-o-tbl' : 'b-a-o-can';
    const callsign = clamp(e.callsign, 256);
    const uid = clamp(e.uid, 256);
    const emg = e.active
        ? `<emergency type="911 Alert">${xmlEsc(callsign)}</emergency>`
        : `<emergency cancel="true">${xmlEsc(callsign)}</emergency>`;
    return (`<event version="2.0" uid="${xmlEsc(uid)}-9-1-1" type="${type}"` +
        ` time="${t}" start="${t}" stale="${stale}" how="m-g">` +
        `<point lat="${e.lat}" lon="${e.lon}" hae="9999999.0" ce="9999999.0" le="9999999.0"/>` +
        `<detail><contact callsign="${xmlEsc(callsign)}"/>${emg}` +
        `<link uid="${xmlEsc(uid)}" type="a-f-G-U-C" relation="p-p"/></detail></event>`);
}
/** Parser et nød-CoT-event. null hvis ikke emergency. */
export function parseEmergencyCot(xml) {
    if (xml.length > MAX_COT_MSG_BYTES)
        return null;
    const ev = /<event\b[^>]*>/i.exec(xml);
    if (!ev)
        return null;
    const type = attr(ev[0], 'type') ?? '';
    if (!/^b-a-o-/.test(type))
        return null;
    const pt = /<point\b[^>]*\/?>/i.exec(xml);
    const uidRaw = attr(ev[0], 'uid') ?? '';
    const lat = pt ? Number(attr(pt[0], 'lat')) : NaN;
    const lon = pt ? Number(attr(pt[0], 'lon')) : NaN;
    if (!uidRaw || !Number.isFinite(lat) || !Number.isFinite(lon))
        return null;
    if (lat < -90 || lat > 90 || lon < -180 || lon > 180)
        return null;
    const csM = /<contact\b[^>]*\bcallsign="([^"]*)"/i.exec(xml);
    const emg = /<emergency\b[^>]*>/i.exec(xml);
    const cancelled = type === 'b-a-o-can' || (emg ? attr(emg[0], 'cancel') === 'true' : false);
    const timeStr = attr(ev[0], 'time') ?? attr(ev[0], 'start');
    const tsMs = timeStr ? Date.parse(timeStr) : NaN;
    const uid = xmlUnesc(uidRaw).replace(/-9-1-1$/, '');
    return {
        uid,
        callsign: csM ? xmlUnesc(csM[1]) : uid,
        lat, lon,
        tsMs: Number.isFinite(tsMs) ? tsMs : 0,
        active: !cancelled,
    };
}
// ── Stridsmelding → CoT-markør (så ATAK ser SKADE-meldinger) ────────────────
/** ATAK-CoT-type per stridsmeldingstype. */
const REPORT_COT_TYPE = {
    MEDEVAC: 'b-r-f-h-c', // CASEVAC
    CONTACT: 'a-h-G', // fiendtlig bakkekontakt
    SALUTE: 'a-h-G',
    SITREP: 'b-m-p-s-m', // spot-markør
};
/** Stridsmelding → CoT-event (XML) med sammendrag i remarks. */
export function reportToCotXml(r, opts = {}) {
    const t = iso(r.tsMs);
    const stale = iso(r.tsMs + (opts.staleSec ?? 3600) * 1000);
    const type = REPORT_COT_TYPE[r.type] ?? 'b-m-p-s-m';
    const remarks = `${r.type}: ${reportSummary(r)}`;
    return (`<event version="2.0" uid="skade-report-${xmlEsc(r.id)}" type="${type}"` +
        ` time="${t}" start="${t}" stale="${stale}" how="m-g">` +
        `<point lat="${r.lat}" lon="${r.lon}" hae="9999999.0" ce="9999999.0" le="9999999.0"/>` +
        `<detail><contact callsign="${xmlEsc(r.type)}"/>` +
        `<remarks>${xmlEsc(remarks)}</remarks></detail></event>`);
}
//# sourceMappingURL=cotmsg.js.map